import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { Col, Row, Space, Spin, notification, Tooltip, Dropdown } from "antd";
import { ShareAltOutlined, LikeOutlined, LikeFilled, DislikeOutlined, DislikeFilled, LoadingOutlined, ArrowDownOutlined, DownloadOutlined, FileTextOutlined, PrinterOutlined } from "@ant-design/icons";
import {
  ThemeFilterBtn,
  ThemeMainWrapper,
  ThemePara1,
} from "../Styles/Theme";
import {
  ProjectGptChatMassage,
  InsightComposerShell,
  InsightStyledTextArea,
  InsightSendButton,
  FeedbackButtonsWrapper,
  FeedbackButton,
  ScrollToBottomButton,
} from "./ProjectsStyle";
import { useNavigate, useParams } from "react-router-dom";
import MobHeader from "../../Components/Header/MobHeader";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MDXRenderer } from "../../Components/MDXComponents";
import {
  getChatApi,
  getChatReset,
  streamMessageApi,
  abortStreamApi,
  updateMessageFeedbackApi,
  updateChatTitleApi,
  getChatProcessingStatusApi,
  reconnectStreamApi,
} from "../../store/actions/chatAction";
import ShareModal from "../../Components/ShareModal/ShareModal";
import EditableTitle from "../../Components/common/EditableTitle";
import { useInsightsPermissions, useUserInfo, USER_TYPES } from "../../utils/permissions";
import {
  isChatSharedToMe,
  hasSharePermission,
} from "../../utils/sharePermissions";
import Lottie from "lottie-react";
import excelAnimation from "../../jsons/insight-engine/excel-animation.json";
import pdfAnimation from "../../jsons/insight-engine/pdf-animation.json";
import docAnimation from "../../jsons/insight-engine/doc-animation.json";
import txtAnimation from "../../jsons/insight-engine/txt-animation.json";
import jsonAnimation from "../../jsons/insight-engine/json-animation.json";
import genericAnimation from "../../jsons/insight-engine/generic.json";
import deepThinkSignalAnimation from "../../jsons/insight-engine/deep-think-signal.json";
import axios from "axios";
import config from "../../config";
import { getUserToken } from "../../utils/helpers";

// Check if content contains interactive components (MDX syntax)
// Defined outside component to prevent recreation on every render
const hasInteractiveComponents = (content) => {
  if (!content) return false;
  // Check for component blocks: ```component:Type or <ComponentName
  return /```component:\w+|<(Chart|Table|DataTable|Collapse|Tabs|DataExplorer|InteractiveChart|InteractiveTable|CollapsibleSection|Explorer|StatGrid|Stats)\s/i.test(content);
};

// Markdown components configuration - defined outside to prevent recreation
const markdownComponents = {
  h1: ({ children }) => <h1 className="text-2xl font-bold my-4">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-bold my-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-bold my-2">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-bold my-2">{children}</h4>,
  h5: ({ children }) => <h5 className="text-sm font-bold my-1">{children}</h5>,
  h6: ({ children }) => <h6 className="text-xs font-bold my-1">{children}</h6>,
  p: ({ children }) => <p className="my-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 my-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 my-2">{children}</ol>,
  li: ({ children }) => <li className="my-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote
      className="pl-4 my-3 italic text-gray-700 py-3 rounded-r"
      style={{ borderLeft: '4px solid #3D2969', backgroundColor: 'rgba(61, 41, 105, 0.06)' }}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, className, children }) => {
    if (inline) {
      return <code className="bg-gray-100 px-1 rounded text-sm">{children}</code>;
    }
    return (
      <pre className="bg-gray-100 p-3 rounded overflow-x-auto my-2">
        <code className={className}>{children}</code>
      </pre>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full border border-gray-300">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-gray-300 px-4 py-2 bg-gray-50 font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-gray-300 px-4 py-2">{children}</td>,
  hr: () => <hr className="my-4 border-gray-300" />,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  img: ({ src, alt, ...props }) => (
    <img
      src={src}
      alt={alt || "Chart"}
      className="max-w-full h-auto my-4 rounded-lg shadow-md"
      style={{ maxHeight: "500px" }}
      loading="lazy"
      {...props}
    />
  ),
};

// ContentRenderer component - defined outside to prevent recreation on every render
// This prevents React's "Maximum update depth exceeded" error during streaming
const ContentRenderer = ({ content, sandboxMetadata }) => {
  // Use MDXRenderer if content has interactive components or we have sandbox metadata with plot_data
  if (hasInteractiveComponents(content) || sandboxMetadata?.plot_data?.length > 0) {
    return <MDXRenderer content={content} sandboxMetadata={sandboxMetadata} />;
  }

  // Fallback to simple ReactMarkdown for plain markdown content
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
};

const getFileAnimation = (fileType) => {
  const type = (fileType || '').toLowerCase();
  switch (type) {
    case 'xls': case 'xlsx': case 'csv': return excelAnimation;
    case 'pdf': return pdfAnimation;
    case 'doc': case 'docx': return docAnimation;
    case 'txt': return txtAnimation;
    case 'json': return jsonAnimation;
    default: return genericAnimation;
  }
};

// Final-stage CoT shown on page reload when backend is still processing in background.
// Shows the last stage immediately — no step-by-step replay, consistent across reloads.
const RELOAD_COT_STEPS = [
  { step: 1, icon: 'brain', text: 'Engaging Recursive Reasoning Engine' },
  { step: 5, icon: 'insight', text: 'Generating insights...' },
];

const InsightEngineChat = (props) => {
  const {
    getChat,
    getChatReset,
    getChatState,
    abortStream,
    updateChatTitle,
  } = props;

  const navigate = useNavigate();
  const { chatId } = useParams();
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const scrollDebounceRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const reconnectAbortRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const streamingContentRef = useRef("");
  const deepThinkTimerRef = useRef(null);

  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  // Sandbox metadata: charts, tables, plot_data from quantitative analysis
  const [pendingSandboxMetadata, setPendingSandboxMetadata] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  // Chain of Thought state
  const [cotSteps, setCotSteps] = useState([]);
  const [cotCollapsed, setCotCollapsed] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState({}); // { [index]: 'up' | 'down' | null }
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showDeepThinkBanner, setShowDeepThinkBanner] = useState(false);

  // Permission checks
  const { can } = useInsightsPermissions();
  const { userId, userType } = useUserInfo();

  // Compute permissions based on ownership and share status
  const isSystemAdmin = userType === USER_TYPES.SUPER_ADMIN;
  const isOwner = chat?.created_by?._id === userId || chat?.created_by === userId;
  const isSharedToMe = isChatSharedToMe(chat);
  const sharePerms = chat?.sharePermissions;

  // Permission helpers (same pattern as ProjectDetails):
  // - System admins always have full access
  // - First check role-based permission (module/submodule access)
  // - Then check if owner OR (shared with share permission)
  // This ensures role permission is ALWAYS checked first
  const canSendMessages = isSystemAdmin ||
    (can('send-message') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_send_messages'))));
  const canShareChat = isSystemAdmin ||
    (can('share') && (isOwner || (isSharedToMe && hasSharePermission(sharePerms, 'can_share'))));

  useEffect(() => {
    document.body.classList.add("bodyChatGPT");
    return () => {
      document.body.classList.remove("bodyChatGPT");
    };
  }, []);

  useEffect(() => {
    if (chatId) {
      // Reset for new chat load
      initialLoadDoneRef.current = false;
      getChat(chatId);
    }
    return () => {
      getChatReset();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      stopReconnection();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (deepThinkTimerRef.current) {
        clearTimeout(deepThinkTimerRef.current);
        deepThinkTimerRef.current = null;
      }
    };
  }, [chatId]);

  // Check if user is near bottom of scroll container
  const isNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;
    const threshold = 150;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  useEffect(() => {
    if (getChatState.apiState === "success") {
      const chatData = getChatState.data?.data || null;
      setChat(chatData);
      setMessages(chatData?.messages || []);
      setLoading(false);
      initialLoadDoneRef.current = true;

      // Detect if backend is still processing a query (e.g., page was reloaded mid-processing)
      if (chatData?.processingStatus === 'processing' && !isStreaming && !reconnectAbortRef.current) {
        setIsStreaming(true);
        setIsThinking(true);
        setCotCollapsed(false);
        setShowDeepThinkBanner(true);
        const storedSteps = chatData.processingCotSteps;
        setCotSteps(storedSteps?.length > 0 ? storedSteps : RELOAD_COT_STEPS);
        startReconnectStream(chatId);
      }
    } else if (getChatState.apiState === "error") {
      notification.error({
        message: "Error",
        description: getChatState.message || "Failed to load chat",
      });
      setLoading(false);
    } else if (getChatState.apiState === "loading") {
      // Only show loading on initial load (prevents blink on refresh)
      if (!initialLoadDoneRef.current) {
        setLoading(true);
      }
    }
  }, [getChatState]);

  // Handle scroll events with debounce for button visibility
  const handleScroll = useCallback(() => {
    const nearBottom = isNearBottom();

    // Track if user scrolled up during streaming
    if (isStreaming) {
      userScrolledUpRef.current = !nearBottom;
    }

    // Debounce button visibility to prevent jittering
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = setTimeout(() => {
      setShowScrollButton(!nearBottom);
    }, 150);
  }, [isStreaming, isNearBottom]);

  // Scroll only when new messages are added (length increases)
  const prevLengthRef = useRef(messages.length);
  useEffect(() => {
    const isNewMessage = messages.length > prevLengthRef.current;
    prevLengthRef.current = messages.length;

    if (isNewMessage && messages.length > 0 && !userScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [messages.length]);

  // Scroll during streaming using requestAnimationFrame to avoid state update cascades
  const streamingScrollRef = useRef(null);
  useEffect(() => {
    if (isStreaming && streamingContent && !userScrolledUpRef.current) {
      if (streamingScrollRef.current) {
        cancelAnimationFrame(streamingScrollRef.current);
      }
      streamingScrollRef.current = requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "auto",
          block: "end",
        });
      });
    }
    return () => {
      if (streamingScrollRef.current) {
        cancelAnimationFrame(streamingScrollRef.current);
      }
    };
  }, [streamingContent, isStreaming]);

  useEffect(() => {
    if (isThinking && cotSteps.length > 0 && !userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    }
  }, [isThinking, cotSteps]);

  const scrollToBottom = useCallback((smooth = false) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
      block: "end"
    });
    setShowScrollButton(false);
  }, []);

  const handleScrollToBottom = useCallback(() => {
    userScrolledUpRef.current = false;
    scrollToBottom(true);
  }, [scrollToBottom]);

  const focusInput = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const stopReconnection = useCallback(() => {
    if (reconnectAbortRef.current) {
      reconnectAbortRef.current.abort();
      reconnectAbortRef.current = null;
    }
  }, []);

  // Polling fallback for when background stream is not available (e.g., server restart)
  const startPollingFallback = useCallback((targetChatId) => {
    if (pollingIntervalRef.current) return;
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await getChatProcessingStatusApi(targetChatId);
        if (status === 'idle' || status === null) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsStreaming(false);
          setIsThinking(false);
          setCotSteps([]);
          setShowDeepThinkBanner(false);
          getChat(targetChatId);
          focusInput();
        } else if (status === 'failed') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsStreaming(false);
          setIsThinking(false);
          setCotSteps([]);
          setShowDeepThinkBanner(false);
          notification.error({
            message: 'Error',
            description: 'Analysis failed. Please try sending your query again.',
          });
          getChat(targetChatId);
          focusInput();
        }
      } catch (err) {
        // Continue polling
      }
    }, 3000);
  }, [getChat]);

  const startReconnectStream = useCallback((targetChatId) => {
    const controller = new AbortController();
    reconnectAbortRef.current = controller;

    reconnectStreamApi(
      targetChatId,
      controller.signal,
      (data) => {
        console.log('[ReconnectStream] Received event:', data.type, data.type === 'content' ? data.content?.substring(0, 50) : data);
        if (data.type === 'content') {
          setIsThinking(false);
          setCotSteps([]);
          setCotCollapsed(true);
          setShowDeepThinkBanner(false);
          streamingContentRef.current += data.content || '';
          setStreamingContent(streamingContentRef.current);
        } else if (data.type === 'done') {
          if (streamingContentRef.current) {
            const assistantMessage = {
              _id: `temp-assistant-${Date.now()}`,
              role: 'assistant',
              content: streamingContentRef.current,
              createdAt: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMessage]);
          }
          setStreamingContent('');
          streamingContentRef.current = '';
          setIsStreaming(false);
          setIsThinking(false);
          setCotSteps([]);
          setShowDeepThinkBanner(false);
          reconnectAbortRef.current = null;
          getChat(targetChatId);
          focusInput();
        } else if (data.type === 'error') {
          setStreamingContent('');
          streamingContentRef.current = '';
          setIsStreaming(false);
          setIsThinking(false);
          setCotSteps([]);
          setShowDeepThinkBanner(false);
          reconnectAbortRef.current = null;
          notification.error({
            message: 'Error',
            description: data.error || 'Analysis failed. Please try sending your query again.',
          });
          getChat(targetChatId);
          focusInput();
        } else if (data.type === 'already_complete') {
          setIsStreaming(false);
          setIsThinking(false);
          setCotSteps([]);
          setShowDeepThinkBanner(false);
          reconnectAbortRef.current = null;
          getChat(targetChatId);
          focusInput();
        } else if (data.type === 'no_active_stream') {
          // Rare edge case — fall back to polling
          reconnectAbortRef.current = null;
          startPollingFallback(targetChatId);
        }
      },
      (error) => {
        if (error === 'AbortError') return;
        reconnectAbortRef.current = null;
        setIsStreaming(false);
        setIsThinking(false);
        setCotSteps([]);
        setShowDeepThinkBanner(false);
        getChat(targetChatId);
        focusInput();
      },
      () => {
        reconnectAbortRef.current = null;
      }
    );
  }, [getChat, startPollingFallback]);

  const handleStreamMessage = useCallback(async (content) => {
    if (!content.trim() || isStreaming) return;

    // Reset scroll tracking for new message
    userScrolledUpRef.current = false;

    const userMessage = {
      _id: `temp-${Date.now()}`,
      role: "user",
      content: content.trim(),
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setNewMessage("");
    setIsStreaming(true);
    setIsThinking(true);
    setCotSteps([]);
    setCotCollapsed(false);
    setStreamingContent("");
    streamingContentRef.current = "";
    setShowDeepThinkBanner(false);
    if (deepThinkTimerRef.current) clearTimeout(deepThinkTimerRef.current);
    deepThinkTimerRef.current = setTimeout(() => {
      setShowDeepThinkBanner(true);
    }, 120000);

    abortControllerRef.current = new AbortController();

    try {
      await streamMessageApi(
        chatId,
        content.trim(),
        abortControllerRef.current.signal,
        (data) => {
          if (data.type === "chain_of_thought") {
            setIsThinking(true);
            setCotSteps(prev => {
              // Steps 0 and 1 are header updates - replace the header
              if (data.step <= 1) {
                const withoutHeader = prev.filter(s => s.step > 1);
                return [data, ...withoutHeader];
              }
              // Avoid duplicate steps
              if (prev.find(s => s.step === data.step)) return prev;
              // Reading steps replace previous reading steps (only one visible at a time)
              if (data.phase === 'reading') {
                const filtered = prev.filter(s => s.phase !== 'reading');
                return [...filtered, data];
              }
              // "Read all" summary replaces any remaining reading steps
              if (data.replacesReading) {
                const filtered = prev.filter(s => s.phase !== 'reading');
                return [...filtered, data];
              }
              return [...prev, data];
            });
          } else if (data.type === "thinking") {
            // Legacy fallback
            setIsThinking(true);
          } else if (data.type === "content") {
            // When first content arrives, immediately hide the thinking/CoT widget
            setIsThinking(false);
            setCotSteps([]);
            setCotCollapsed(true);
            setShowDeepThinkBanner(false);
            if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
            streamingContentRef.current += data.content || "";
            setStreamingContent(streamingContentRef.current);
          } else if (data.type === "content_done") {
            setIsThinking(false);
            streamingContentRef.current = data.content || streamingContentRef.current;
            setStreamingContent(streamingContentRef.current);
          } else if (data.type === "content_verified") {
            streamingContentRef.current = data.content || streamingContentRef.current;
            setStreamingContent(streamingContentRef.current);
          } else if (data.type === "sandbox_metadata") {
            // Store sandbox results (charts, tables, plot_data) for display
            console.log("[InsightEngineChat] Received sandbox_metadata:", data);
            setPendingSandboxMetadata({
              charts: data.charts || [],
              tables: data.tables || [],
              plot_data: data.plot_data || [],
              validation: data.validation || null,
              execution_time_ms: data.execution_time_ms,
              survey_warnings: data.survey_warnings || null,
              trend_warnings: data.trend_warnings || null,
            });
          } else if (data.type === "done") {
            if (streamingContentRef.current) {
              const assistantMessage = {
                _id: `temp-assistant-${Date.now()}`,
                role: "assistant",
                content: streamingContentRef.current,
                createdAt: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, assistantMessage]);
            }
            setStreamingContent("");
            setIsStreaming(false);
            setIsThinking(false);
            setCotSteps([]);
            setPendingSandboxMetadata(null);
            setShowDeepThinkBanner(false);
            if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
            getChat(chatId);
            focusInput();
          } else if (data.type === "error") {
            notification.error({
              message: "Error",
              description: data.error || "Something went wrong",
            });
            setStreamingContent("");
            setIsStreaming(false);
            setIsThinking(false);
            setCotSteps([]);
            setPendingSandboxMetadata(null);
            setShowDeepThinkBanner(false);
            if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
            focusInput();
          }
        },
        (error) => {
          if (error !== "AbortError") {
            notification.error({
              message: "Error",
              description: error || "Failed to send message",
            });
          }
          setStreamingContent("");
          setIsStreaming(false);
          setIsThinking(false);
          setCotSteps([]);
          setShowDeepThinkBanner(false);
          if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
          getChat(chatId);
          focusInput();
        },
        () => {
          setIsStreaming(false);
          setIsThinking(false);
          setShowDeepThinkBanner(false);
          if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
          focusInput();
        }
      );
    } catch (error) {
      setStreamingContent("");
      setIsStreaming(false);
      setIsThinking(false);
      setCotSteps([]);
      setShowDeepThinkBanner(false);
      if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
      focusInput();
    }
  }, [chatId, isStreaming, getChat]);

  const handleSend = () => {
    if (newMessage.trim() && !isStreaming) {
      handleStreamMessage(newMessage);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortStream(chatId);
    stopReconnection();
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsStreaming(false);
    setIsThinking(false);
    setCotSteps([]);
    setShowDeepThinkBanner(false);
    if (deepThinkTimerRef.current) { clearTimeout(deepThinkTimerRef.current); deepThinkTimerRef.current = null; }
    focusInput();
  };

  const handleFeedback = useCallback(async (messageId, currentFeedback, newFeedback) => {
    // Toggle behavior: if clicking same feedback, remove it (set to null)
    const feedbackValue = currentFeedback === newFeedback ? null : newFeedback;

    // Set loading state for specific button (up or down)
    setFeedbackLoading((prev) => ({ ...prev, [messageId]: newFeedback }));

    try {
      await updateMessageFeedbackApi(chatId, messageId, feedbackValue);

      // Update local state (no scroll triggered since length doesn't change)
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === messageId ? { ...msg, feedback: feedbackValue } : msg
        )
      );
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update feedback",
      });
    } finally {
      setFeedbackLoading((prev) => ({ ...prev, [messageId]: null }));
    }
  }, [chatId]);

  const [downloadingPdf, setDownloadingPdf] = useState(null); // messageId of currently downloading

  const handleDownloadPdf = useCallback(async (assistantMessageId, paginated = false) => {
    const assistantEl = document.querySelector(`[data-message-id="${assistantMessageId}"]`);
    if (!assistantEl) return;

    setDownloadingPdf(assistantMessageId);
    try {
      // Find user question
      const visibleMessages = messages.filter((m) => !m.isHiddenFromUser && !m.isSummary);
      const idx = visibleMessages.findIndex((m) => m._id === assistantMessageId);
      let userQuestion = '';
      for (let i = idx - 1; i >= 0; i--) {
        if (visibleMessages[i].role === 'user') { userQuestion = visibleMessages[i].content; break; }
      }

      // Clone response content and get original width
      const messageTextEl = assistantEl.querySelector('.messageText');
      if (!messageTextEl) return;
      const origWidth = Math.round(messageTextEl.getBoundingClientRect().width);
      const clone = messageTextEl.cloneNode(true);

      // Show all hidden table rows (pagination hides non-current-page rows)
      clone.querySelectorAll('.pdf-hidden-row').forEach((el) => {
        el.style.display = '';
        el.classList.remove('pdf-hidden-row');
      });

      // Remove search containers (input + parent .relative div with icon)
      clone.querySelectorAll('input[placeholder="Search..."]').forEach((el) => {
        const wrapper = el.closest('.relative');
        if (wrapper) wrapper.remove(); else el.remove();
      });

      // Remove export button containers (button + parent .relative div)
      clone.querySelectorAll('[title="Export data"]').forEach((el) => {
        const wrapper = el.closest('.relative');
        if (wrapper) wrapper.remove(); else el.remove();
      });

      // Remove sort icons from table headers
      clone.querySelectorAll('th .flex.flex-col').forEach((el) => el.remove());

      // Remove pagination info and controls
      clone.querySelectorAll('.border-t.flex.justify-between').forEach((el) => el.remove());

      // Remove DataExplorer view switcher (Table/Bar/Line/Pie buttons)
      clone.querySelectorAll('.bg-gray-100.p-1.rounded-lg').forEach((el) => {
        if (el.querySelector('button')) el.remove();
      });

      // Remove DataExplorer filter section (dropdowns, range sliders)
      clone.querySelectorAll('.flex-wrap.gap-2.mt-3').forEach((el) => el.remove());

      // Remove Ant Design pagination if present
      clone.querySelectorAll('.ant-pagination').forEach((el) => el.remove());

      // Convert remaining buttons to static spans (preserves chart legends, section titles, tab labels)
      clone.querySelectorAll('button').forEach((btn) => {
        const span = document.createElement('span');
        span.innerHTML = btn.innerHTML;
        span.className = btn.className;
        if (btn.style.cssText) span.style.cssText = btn.style.cssText;
        btn.parentNode.replaceChild(span, btn);
      });

      // Expand all collapsed sections so content is visible in PDF
      clone.querySelectorAll('[style]').forEach((el) => {
        if (el.style.maxHeight === '0px') {
          el.style.maxHeight = 'none';
          el.style.opacity = '1';
        }
      });

      // Remove empty flex containers left after search/export removal
      clone.querySelectorAll('.flex.items-center.gap-2').forEach((el) => {
        if (el.children.length === 0) el.remove();
      });

      // Extract styles from CSSOM (styled-components uses insertRule)
      const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((el) => {
          if (el.sheet) {
            try {
              return `<style>${Array.from(el.sheet.cssRules).map((r) => r.cssText).join('\n')}</style>`;
            } catch (e) { return el.outerHTML; }
          }
          return el.outerHTML;
        }).join('\n');

      const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // Build full HTML document for Puppeteer
      const html = `<!DOCTYPE html><html><head>${styles}
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 40px; background: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px; color: rgba(0,0,0,0.85); line-height: 1.5715; }
  .q-box { background: #f3f0ff; padding: 16px 20px; border-radius: 12px; margin-bottom: 24px; max-width: ${origWidth}px; }
  .q-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #6b5ce7; margin-bottom: 8px; }
  .q-text { font-size: 14px; line-height: 1.6; white-space: pre-wrap; }
  .content-wrap { width: ${origWidth}px; }
  [title="Export data"], input { display: none !important; }
  .pdf-hidden-row { display: table-row !important; }
  table, svg, pre, .border, .rounded-lg, .grid { page-break-inside: avoid; }
  h1, h2, h3, h4, h5, h6, p { page-break-after: avoid; }
</style></head><body>
${userQuestion ? `<div class="q-box"><div class="q-label">Question</div><div class="q-text">${esc(userQuestion)}</div></div>` : ''}
<div class="content-wrap">${clone.innerHTML}</div>
</body></html>`;

      // Send to backend for Puppeteer PDF generation
      const response = await axios.post(
        `${config.api.base_url}/chats/generate-pdf`,
        { html, paginated },
        { responseType: 'blob', headers: { Authorization: getUserToken() } }
      );

      // Auto-download the PDF
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `insight-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      notification.error({ message: 'Error', description: 'Failed to generate PDF' });
    } finally {
      setDownloadingPdf(null);
    }
  }, [messages]);

  const handleSaveChatTitle = async (newTitle) => {
    await updateChatTitle(chatId, newTitle);
    setChat((prev) => ({ ...prev, title: newTitle }));
  };

  if (loading) {
    return (
      <>
        <MobHeader />
        <ThemeMainWrapper className="hasHeader smallPadding">
          <div className="flex justify-center items-center h-96">
            <Spin size="large" />
          </div>
        </ThemeMainWrapper>
      </>
    );
  }

  if (!chat) {
    return (
      <>
        <MobHeader />
        <ThemeMainWrapper className="hasHeader smallPadding">
          <div className="text-center py-20">
            <h2>Chat not found</h2>
            <button onClick={() => navigate("/projects/")}>Back to Projects</button>
          </div>
        </ThemeMainWrapper>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes cotFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cotPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes cotScanPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        .cot-skeleton {
          position: relative;
          width: 72px;
          height: 72px;
          background: transparent;
          border-radius: 10px;
          animation: cotFadeIn 0.5s ease-out;
        }
        .cot-skeleton::before,
        .cot-skeleton::after,
        .cot-skeleton .corner-bl,
        .cot-skeleton .corner-br {
          content: '';
          position: absolute;
          width: 22px;
          height: 22px;
          border-color: #d4d4d8;
          border-style: solid;
          animation: cotScanPulse 1.5s ease-in-out infinite, cotCornerFadeOut 0.4s ease-out 3s forwards;
        }
        .cot-skeleton::before {
          top: 0; left: 0;
          border-width: 2px 0 0 2px;
          border-radius: 4px 0 0 0;
        }
        .cot-skeleton::after {
          top: 0; right: 0;
          border-width: 2px 2px 0 0;
          border-radius: 0 4px 0 0;
        }
        .cot-skeleton .corner-bl {
          bottom: 0; left: 0;
          border-width: 0 0 2px 2px;
          border-radius: 0 0 0 4px;
        }
        .cot-skeleton .corner-br {
          bottom: 0; right: 0;
          border-width: 0 2px 2px 0;
          border-radius: 0 0 4px 0;
        }
        .cot-skeleton .lottie-reveal {
          opacity: 0;
          animation: cotLottieReveal 0.5s ease-out 3s forwards;
        }
        @keyframes cotCornerFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes cotLottieReveal {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <MobHeader />
      <ThemeMainWrapper
        className="hasHeader smallPadding"
        title={
          <Space gap={10}>
            <ThemeFilterBtn
              className="shadow"
              onClick={() => navigate(-1)}
              size="middle"
              icon={<img src="/images/back-icon.svg" alt="Back" />}
            />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <EditableTitle
                value={chat.title}
                onSave={handleSaveChatTitle}
                placeholder="New Chat"
              />
              {chat.project?.name && (
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>
                  {chat.project.name}
                </span>
              )}
            </div>
          </Space>
        }
        extra={
          <Space>
            {canShareChat && (
              <ThemeFilterBtn
                size="large"
                shape="square"
                icon={<ShareAltOutlined />}
                onClick={() => setShareModalVisible(true)}
              >
                Share
              </ThemeFilterBtn>
            )}
          </Space>
        }
        actions={[
          <Row gutter={[16, 16]} key="input">
            <Col
              xs={{ span: 24, offset: 0 }}
              sm={{ span: 24, offset: 0 }}
              md={{ span: 18, offset: 3 }}
              lg={{ span: 16, offset: 4 }}
              xl={{ span: 16, offset: 4 }}
              xxl={{ span: 16, offset: 4 }}
            >
              <div style={{ position: 'relative' }}>
                {showDeepThinkBanner && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: 10,
                    zIndex: 10,
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      background: '#f7f6f9',
                      border: '1px solid rgba(61, 41, 105, 0.15)',
                      borderRadius: 30,
                      boxShadow: '0px 0px 10px 0px rgba(61, 41, 105, 0.05)',
                      padding: '7px 16px',
                      whiteSpace: 'nowrap',
                    }}>
                      <div style={{ width: 28, height: 28, flexShrink: 0 }}>
                        <Lottie
                          animationData={deepThinkSignalAnimation}
                          loop={true}
                          speed={2}
                          style={{ width: 28, height: 28 }}
                        />
                      </div>
                      <div>
                        <div style={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 600,
                          fontSize: 12,
                          color: '#1e1e1e',
                          lineHeight: '16px',
                        }}>
                          We're running a complex multi-step analysis. This can take up to 20 minutes.
                        </div>
                        <div style={{
                          fontFamily: "'Inter', sans-serif",
                          fontWeight: 400,
                          fontSize: 11,
                          color: '#64748b',
                          lineHeight: '14px',
                        }}>
                          Feel free to navigate away — we'll keep working in the background.
                        </div>
                      </div>
                      <button
                        onClick={() => setShowDeepThinkBanner(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 2,
                          marginLeft: 2,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                          <path d="M1 1L13 13M13 1L1 13" stroke="#3d2969" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <InsightComposerShell>
                <InsightStyledTextArea
                  ref={inputRef}
                  placeholder={canSendMessages ? "Ask a question to generate insights from your data..." : "You don't have permission to send messages"}
                  disabled={isStreaming || !canSendMessages}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onPressEnter={handleKeyPress}
                />
                {isStreaming ? (
                  <InsightSendButton
                    type="button"
                    onClick={handleAbort}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="12" height="12" fill="currentColor" rx="1"/>
                    </svg>
                  </InsightSendButton>
                ) : (
                  <InsightSendButton
                    type="button"
                    onClick={handleSend}
                    disabled={!newMessage.trim() || !canSendMessages}
                  >
                    <img src="/images/send-icon.svg" alt="Send" />
                  </InsightSendButton>
                )}
              </InsightComposerShell>
              <div className="presEnterLine">
                {!canSendMessages ? "View only - You cannot send messages" : isStreaming ? "Generating response..." : "Press Enter to send • Shift+Enter for a new line"}
              </div>
            </Col>
          </Row>,
        ]}
      >
        <Row>
            <Col
              xs={{ span: 24, offset: 0 }}
              sm={{ span: 24, offset: 0 }}
              md={{ span: 18, offset: 3 }}
              lg={{ span: 16, offset: 4 }}
              xl={{ span: 16, offset: 4 }}
              xxl={{ span: 16, offset: 4 }}
            >
              <div
                className="scrollable"
                style={{ height: 'calc(100vh - 220px)', position: 'relative', overflowX: 'hidden', paddingBottom: showDeepThinkBanner ? 60 : 0 }}
                ref={scrollContainerRef}
                onScroll={handleScroll}
              >
                <ProjectGptChatMassage>
                  {messages.length === 0 && !isStreaming && (
                    <div className="text-center py-20 text-gray-500">
                      <h3 className="text-lg mb-2">Start a conversation</h3>
                      <p>Ask a question about your project data to get started.</p>
                    </div>
                  )}
                  {messages.map((message) => (
                    <div
                      className="messageContainer"
                      key={message._id}
                      data-is-user={message.role === "user" ? "true" : "false"}
                      data-message-id={message._id}
                    >
                      <div>
                        <div className="messageText" style={message.role === "user" ? { whiteSpace: "pre-wrap" } : undefined}>
                          {message.role === "user" ? (
                            message.content
                          ) : (
                            <ContentRenderer
                              content={message.content}
                              sandboxMetadata={message.sandboxMetadata}
                            />
                          )}
                        </div>
                        {message.role === "assistant" && (
                          <FeedbackButtonsWrapper>
                            <Tooltip title="Good response" placement="bottom">
                              <FeedbackButton
                                className={message.feedback === "up" ? "active-up" : ""}
                                onClick={() => handleFeedback(message._id, message.feedback, "up")}
                                disabled={!!feedbackLoading[message._id]}
                              >
                                {feedbackLoading[message._id] === "up" ? (
                                  <LoadingOutlined style={{ fontSize: 14 }} />
                                ) : message.feedback === "up" ? (
                                  <LikeFilled />
                                ) : (
                                  <LikeOutlined />
                                )}
                              </FeedbackButton>
                            </Tooltip>
                            <Tooltip title="Bad response" placement="bottom">
                              <FeedbackButton
                                className={message.feedback === "down" ? "active-down" : ""}
                                onClick={() => handleFeedback(message._id, message.feedback, "down")}
                                disabled={!!feedbackLoading[message._id]}
                                style={{ transform: "scaleX(-1)" }}
                              >
                                {feedbackLoading[message._id] === "down" ? (
                                  <LoadingOutlined style={{ fontSize: 14, transform: "scaleX(-1)" }} />
                                ) : message.feedback === "down" ? (
                                  <DislikeFilled />
                                ) : (
                                  <DislikeOutlined />
                                )}
                              </FeedbackButton>
                            </Tooltip>
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: 'full-page',
                                    icon: <FileTextOutlined />,
                                    label: 'Full Page (No Breaks)',
                                    onClick: () => handleDownloadPdf(message._id, false),
                                  },
                                  {
                                    key: 'printable',
                                    icon: <PrinterOutlined />,
                                    label: 'Printable',
                                    onClick: () => handleDownloadPdf(message._id, true),
                                  },
                                ],
                              }}
                              trigger={['click']}
                              placement="bottomRight"
                              disabled={downloadingPdf === message._id}
                            >
                              <FeedbackButton disabled={downloadingPdf === message._id}>
                                {downloadingPdf === message._id ? (
                                  <LoadingOutlined style={{ fontSize: 14 }} />
                                ) : (
                                  <DownloadOutlined />
                                )}
                              </FeedbackButton>
                            </Dropdown>
                          </FeedbackButtonsWrapper>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Chain of Thought UI */}
                  {cotSteps.length > 0 && (
                    <div className="messageContainer" data-is-user="false">
                      <div>
                        <div className="messageText" style={{ background: 'transparent', boxShadow: 'none', padding: '8px 0' }}>
                          {/* CoT Header */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              cursor: cotSteps.some(s => s.step >= 2) ? 'pointer' : 'default',
                              paddingBottom: 4,
                            }}
                            onClick={() => {
                              if (cotSteps.some(s => s.step >= 2)) {
                                setCotCollapsed(!cotCollapsed);
                              }
                            }}
                          >
                            {/* Brain icon - only show for Chain of Thought, not Thinking */}
                            {cotSteps[cotSteps.length - 1]?.step > 1 && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                                <path d="M8 12V3.33333" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 8.66667C9.4232 8.49806 8.91656 8.14708 8.556 7.66633C8.19544 7.18558 8.00036 6.60094 8 6C7.99964 6.60094 7.80456 7.18558 7.444 7.66633C7.08344 8.14708 6.5768 8.49806 6 8.66667" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M11.732 4.33333C11.8854 4.06766 11.9756 3.77025 11.9957 3.46413C12.0158 3.158 11.9652 2.85136 11.8478 2.56793C11.7304 2.28449 11.5494 2.03187 11.3187 1.82959C11.0881 1.62731 10.814 1.4808 10.5176 1.4014C10.2213 1.32199 9.91069 1.31182 9.6098 1.37168C9.30891 1.43153 9.02583 1.5598 8.78244 1.74656C8.53906 1.93333 8.3419 2.17358 8.20623 2.44873C8.07055 2.72388 7.99999 3.02655 8 3.33333C8.00001 3.02655 7.92945 2.72388 7.79377 2.44873C7.6581 2.17358 7.46094 1.93333 7.21756 1.74656C6.97417 1.5598 6.69109 1.43153 6.3902 1.37168C6.08931 1.31182 5.77868 1.32199 5.48236 1.4014C5.18603 1.4808 4.91193 1.62731 4.68129 1.82959C4.45064 2.03187 4.26961 2.28449 4.15222 2.56793C4.03483 2.85136 3.98421 3.158 4.00429 3.46413C4.02436 3.77025 4.11459 4.06766 4.268 4.33333" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M11.998 3.41667C12.3899 3.51742 12.7537 3.70603 13.0618 3.96821C13.37 4.23038 13.6145 4.55925 13.7768 4.9299C13.939 5.30055 14.0148 5.70326 13.9984 6.10754C13.982 6.51182 13.8738 6.90706 13.682 7.26333" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 12C12.587 12 13.1576 11.8063 13.6233 11.4489C14.089 11.0916 14.4238 10.5905 14.5757 10.0235C14.7276 9.45653 14.6882 8.85524 14.4636 8.31291C14.239 7.77058 13.8417 7.31753 13.3333 7.024" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.3113 11.6553C13.3581 12.0168 13.3302 12.3841 13.2294 12.7343C13.1286 13.0846 12.9571 13.4106 12.7255 13.692C12.4938 13.9734 12.2069 14.2043 11.8825 14.3705C11.5581 14.5367 11.2031 14.6346 10.8393 14.6582C10.4756 14.6818 10.1109 14.6306 9.76774 14.5077C9.42458 14.3849 9.11025 14.193 8.84416 13.9438C8.57807 13.6947 8.36588 13.3937 8.22069 13.0594C8.0755 12.725 8.00039 12.3645 8 12C7.99961 12.3645 7.9245 12.725 7.77931 13.0594C7.63412 13.3937 7.42193 13.6947 7.15584 13.9438C6.88975 14.193 6.57542 14.3849 6.23226 14.5077C5.88909 14.6306 5.52438 14.6818 5.16065 14.6582C4.79692 14.6346 4.44189 14.5367 4.11748 14.3705C3.79307 14.2043 3.50618 13.9734 3.27452 13.692C3.04286 13.4106 2.87136 13.0846 2.77059 12.7343C2.66983 12.3841 2.64194 12.0168 2.68867 11.6553" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M4 12C3.413 12 2.8424 11.8063 2.37671 11.4489C1.91102 11.0916 1.57625 10.5905 1.42432 10.0235C1.27239 9.45653 1.31179 8.85524 1.53641 8.31291C1.76103 7.77058 2.15832 7.31753 2.66667 7.024" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M4.002 3.41667C3.61013 3.51742 3.24633 3.70603 2.93815 3.96821C2.62997 4.23038 2.38549 4.55925 2.22323 4.9299C2.06097 5.30055 1.98517 5.70326 2.00159 6.10754C2.01801 6.51182 2.12621 6.90706 2.318 7.26333" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                            <ThemePara1 className="shinyText" style={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 400,
                              fontSize: 14,
                              lineHeight: '20px',
                              margin: 0,
                            }}>
                              {cotSteps[cotSteps.length - 1]?.step <= 1 ? 'Thinking...' : 'Engaging Recursive Reasoning Engine'}
                            </ThemePara1>
                            {cotSteps.some(s => s.step >= 2) && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{
                                transform: cotCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.3s ease',
                                flexShrink: 0,
                              }}>
                                <path d="M4 6L8 10L12 6" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>

                          {/* CoT Steps */}
                          {!cotCollapsed && (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {cotSteps.filter(s => s.step >= 2).map((step) => (
                                <div
                                  key={step.step}
                                  style={{ animation: 'cotFadeIn 0.4s ease-out' }}
                                >
                                  {/* Step row */}
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 0',
                                  }}>
                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, flexShrink: 0 }}>
                                      {step.icon === 'search' && (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M14 14L11.1067 11.1067" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                          <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                      {step.icon === 'document' && (
                                        <svg width="16" height="16" viewBox="0 0 11 13.3333" fill="none">
                                          <path d="M8.16667 0.5H2.83333C1.54467 0.5 0.5 1.54467 0.5 2.83333V10.5C0.5 11.7887 1.54467 12.8333 2.83333 12.8333H8.16667C9.45533 12.8333 10.5 11.7887 10.5 10.5V2.83333C10.5 1.54467 9.45533 0.5 8.16667 0.5Z" stroke="#3d2969" strokeLinecap="round" strokeLinejoin="round"/>
                                          <path d="M3.16667 3.17H7.83333M3.16667 5.83667H7.83333M3.16667 8.50333H5.5" stroke="#3d2969" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                      {step.icon === 'bullet' && (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M8.06667 8.73333C8.43486 8.73333 8.73333 8.43486 8.73333 8.06667C8.73333 7.69848 8.43486 7.4 8.06667 7.4C7.69848 7.4 7.4 7.69848 7.4 8.06667C7.4 8.43486 7.69848 8.73333 8.06667 8.73333Z" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                      {step.icon === 'check' && (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="#3d2969" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                                          <path d="M5.5 8L7.2 9.7L10.5 6.3" stroke="#3d2969" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                      {step.icon === 'insight' && (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M8 1.5L9.4 6.6L14.5 8L9.4 9.4L8 14.5L6.6 9.4L1.5 8L6.6 6.6L8 1.5Z" stroke="#3d2969" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                      {step.icon === 'refresh' && (
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M14 14L11.1067 11.1067" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                          <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#3d2969" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                    </span>
                                    <span style={{
                                      fontFamily: 'Montserrat, sans-serif',
                                      fontWeight: 400,
                                      fontSize: step.step === 2 ? 14 : 13,
                                      color: step.step === cotSteps[cotSteps.length - 1]?.step ? '#3d2969' : '#71717b',
                                      lineHeight: '20px',
                                      animation: isThinking && step.step === cotSteps[cotSteps.length - 1]?.step ? 'cotPulse 2s ease-in-out infinite' : 'none',
                                    }}>
                                      {step.text}
                                    </span>
                                  </div>

                                  {/* File visuals - only show for the LATEST step (replace, not stack) */}
                                  {step.visuals?.type === 'files' && step.visuals.files?.length > 0 && step.step === cotSteps[cotSteps.length - 1]?.step && (
                                    <div style={{
                                      display: 'flex',
                                      gap: 8,
                                      padding: '8px 0 8px 24px',
                                      flexWrap: 'wrap',
                                    }}>
                                      {step.visuals.files.map((file, i) => (
                                        <div key={i} className="cot-skeleton">
                                          <span className="corner-bl" />
                                          <span className="corner-br" />
                                          <div className="lottie-reveal" style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}>
                                            <Lottie
                                              animationData={getFileAnimation(file.type)}
                                              loop={true}
                                              style={{ width: '100%', height: '100%' }}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {isStreaming && streamingContent && (
                    <div className="messageContainer" data-is-user="false">
                      <div>
                        <div className="messageText">
                          <ContentRenderer
                            content={streamingContent}
                            sandboxMetadata={pendingSandboxMetadata}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </ProjectGptChatMassage>
                {showScrollButton && (
                  <ScrollToBottomButton onClick={handleScrollToBottom}>
                    <ArrowDownOutlined />
                  </ScrollToBottomButton>
                )}
              </div>
            </Col>
          </Row>
      </ThemeMainWrapper>

      <ShareModal
        open={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        entityType="chat"
        entityId={chatId}
        entityName={chat?.title}
        isSharedToMe={isSharedToMe}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  getChatState: state.chat.getChatState,
});

const mapDispatchToProps = (dispatch) => ({
  getChat: (chatId) => dispatch(getChatApi(chatId)),
  getChatReset: () => dispatch(getChatReset()),
  abortStream: (chatId) => dispatch(abortStreamApi(chatId)),
  updateChatTitle: (chatId, title) => dispatch(updateChatTitleApi(chatId, title)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InsightEngineChat);
